import React, { useState, useContext, useEffect } from "react";
import { t } from "i18next";
import TabContainer from "../../components/TabContainer/TabContainer";
import CustomTab from "../../components/TabContainer/CustomTab";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import userContext from "../../context/users/UserContext";
import loaderContext from "../../context/loader/LoaderContext";
import toastContext from "../../context/toast/ToastContext";
import hpContext from "../../context/hanging-protocol/HPContext";
import HangingProtocolDetails from "./Details";
import HangingProtocolPrerequisite from "./Prerequisite";
import HangingProtocolLayout from "./Layout";
import { useFormik } from "formik";
import { PrimaryButton } from "@fluentui/react";
import { ACTIONS, TOAST_TYPE, USERTYPES } from "../../constants/Constants"
import { fetchOptionsData, fetchHPData, addHP, editHP, addSysHP, editSysHP, fetchSysHPData, fetchSysOptionsData } from "../../service/HPService";
import { createHPSchema } from "../../Schemas/HPSchema";
import { initialValues, setValidTabStatus, setErrorTabStatus, addTabStatusInitialValue, editTabStatusInitialValue } from "./helpers/InitialData";
import { AuthRoutes } from "../../constants/Constants";
import * as Yup from "yup";
import '../global-styles.css';

const HPFormManager = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { state } = location;
    const [tabStatusInitialValue, setTabStatusInitialValue] = useState(
      addTabStatusInitialValue
    );
    const [breadCrumbItems, setBreadCrumbItems] = useState([]);
    const [updateActiveTab, setUpdateActiveTab] = useState(null);
    const [allErrors, setAllErrors] = useState({});
    const [hpData, setHPData] = useState({});
    const [searchParams] = useSearchParams();
    const { authenticated, loadByUsername, username } = useContext(userContext);
    const { showToastv2 } = useContext(toastContext);
    const { ref, setLoaderRunning } = useContext(loaderContext);
    const {
      studyPrerequisiteMetaTags, 
      seriesConditionMetaTags, 
      imageConditionMetaTags,
      conditionOperands, 
      viewportFlipOptions,
      viewportAlignmentOptions,
      selectedTab,
      submitClicked,
      setStudyPrerequisiteMetaTags,
      setSeriesConditionMetaTags,
      setImageConditionMetaTags,
      setConditionOperands,
      setViewportFlipOptions,
      setViewportAlignmentOptions,
      setSelectedTab,
      setSubmitClicked,
     } = useContext(hpContext);

    const { roles} = useContext(userContext);

     const getBreadCrumbItems = () => {
      if(props.action === ACTIONS.ADD || props.action === ACTIONS.VIEW)
      breadCrumbItems.push({
        text: t("hanging-protocol.list"),
        key: "hanging-protocol-list",
        href: "/hangingprotocol/list",
        });
      if(props.action === ACTIONS.ADD) {
        breadCrumbItems.push({
          text: t("hanging-protocol.add"),
          key: "hanging-protocol-add",
          });
      } else if(props.action === ACTIONS.EDIT) {
        let id = searchParams.get("id");
        breadCrumbItems.pop();
        breadCrumbItems.push({
          text: t("hanging-protocol.edit"),
          key: "hanging-protocol-edit",
          });
          
      } else if(props.action === ACTIONS.VIEW) {
        breadCrumbItems.push({
          text: t("hanging-protocol.view"),
          key: "hanging-protocol-view"
          });
      }
      
    };

    const convertMapToOptionsArray = (optionMap) => {
      const options = Object.entries(optionMap).map(([key, value]) => ({
        key: key,
        label: value
      }));
      return options;
    }
  
    const getAddHPData = async () => {
      try {
          setLoaderRunning(true);
          ref.current.continuousStart();
          setOptions();
          setLoaderRunning(false);
          ref.current.complete();
      } catch (e) {
        setLoaderRunning(false);
        ref.current.complete();
      }
    };
  
    const getEditViewHPData = async (action) => {
      setLoaderRunning(true);
      ref.current.continuousStart();
      if(action === ACTIONS.EDIT) {
        setOptions();
        setTabStatusInitialValue(editTabStatusInitialValue);
      }
      let id = searchParams.get("id");
      let response;
       if (roles[0] === USERTYPES.ADMIN) {
        response = await fetchSysHPData(id);
      } else {
        response = await fetchHPData(id);
      }
      
      if (response.status === 200 && response.data.result === "SUCCESS") {
        setHPData(response.data.data);
      }
      setLoaderRunning(false);
      ref.current.complete();
      
    };
  
    const setOptions = async () => {
      if (props.action === ACTIONS.ADD || props.action === ACTIONS.EDIT) {
        let response = await fetchOptionsData();
         if (roles[0] === USERTYPES.ADMIN) {
            response =  await fetchSysOptionsData();
          } else {
            response  = await fetchOptionsData();
          }
        if (response.status === 200 && response.data.result === "SUCCESS") {
          const studyPrerequisiteOpts = convertMapToOptionsArray(response.data.data.studyPrerequisiteMetaTags);
          const seriesConditionOpts = convertMapToOptionsArray(response.data.data.seriesConditionsMetaTags);
          const imageConditionOpts = convertMapToOptionsArray(response.data.data.imageConditionsMetaTags);
          const conditionOperandsOpts = convertMapToOptionsArray(response.data.data.conditionOperands);
          const viewportFlipOpts = convertMapToOptionsArray(response.data.data.viewportFlipOptions);
          const viewportAlignmentOpts = convertMapToOptionsArray(response.data.data.viewportAlignmentOptions);

          setStudyPrerequisiteMetaTags(studyPrerequisiteOpts);
          setSeriesConditionMetaTags(seriesConditionOpts);
          setImageConditionMetaTags(imageConditionOpts);
          setConditionOperands(conditionOperandsOpts);
          setViewportFlipOptions(viewportFlipOpts);
          setViewportAlignmentOptions(viewportAlignmentOpts);
        }
      }
    }
  
    let initialData = initialValues(props.action, hpData);
    
    useEffect(() => {
        if (!authenticated) {
          loadByUsername();
        }
      }, [authenticated, loadByUsername, props.action]);

    useEffect(() => {
      getBreadCrumbItems();
      console.log("populateInitialDefaultData");
      if (props.action === ACTIONS.ADD) {
        getAddHPData();
      } else if (props.action === ACTIONS.EDIT || props.action === ACTIONS.VIEW) {
        getEditViewHPData(props.action);
      }
      
    }, [props.action]);

    const HPSchema = createHPSchema(t);
    const tabs = {
      tab1: ['name', 'description'],
      tab2: ['studyPrerequisiteList'],
      tab3: ['layout']
    };

    const validationSchema = Yup.object(
      Object.fromEntries(tabs['tab' + selectedTab].map((field) => [field, HPSchema.fields[field]]))
    );  
    const formik = useFormik({
      initialValues: initialData,
      validationSchema: validationSchema,
      enableReinitialize: true,
      validateOnChange: true,
      validateOnBlur: false,
      onSubmit: async (values) => {
        if (submitClicked === true) {
          try {
            setLoaderRunning(true);
            ref.current.continuousStart();
            let msg = "";
            let response;
            if(props.action === ACTIONS.ADD){
              if (roles[0] === USERTYPES.ADMIN) {
                response = await addSysHP(values);
              } else {
                response = await addHP(values);
              }
              msg = t("string-literals.hanging-protocol-created");
            } else if(props.action === ACTIONS.EDIT){
              if (roles[0] === USERTYPES.ADMIN) {
                response = await editSysHP(values);
              } else {
                response = await editHP(values);
              }
              msg = t("string-literals.hanging-protocol-updated");
            }
            if (response.status === 200 && response.data.result === "SUCCESS") {
              setLoaderRunning(false);
              ref.current.complete();
              showToastv2(
                TOAST_TYPE.SUCCESS,
                msg
              );
              window.location.assign(AuthRoutes.VIEWHANGINGPROTOCOL + "?id=" + response.data.data.id);
              // navigate(AuthRoutes.VIEWHANGINGPROTOCOL + "?id=" + response.data.data.id);
            } else {
              setLoaderRunning(false);
              ref.current.complete();
              showToastv2(TOAST_TYPE.ERROR, response.data.message);
            }
            
          } catch (e) {
            setLoaderRunning(false);
            ref.current.complete();
          }
          
        }
      },
    });

    const {
        values,
        handleSubmit,
        validateForm,
        errors,
    } = formik;

    const handleCancel = () => {
      navigate(-1);
    }

    const handleSave = async () => {
      setSubmitClicked(true);
      if (Object.keys(errors).length === 0) {
        handleSubmit();
        let id = searchParams.get("id");
      }
    }

    const handleLoadEdit = () => {
      let id = searchParams.get("id");
      navigate(AuthRoutes.EDITHANGINGPROTOCOL + "?id=" + id);
    }

    const handleLoadView = () => {
      let id = searchParams.get("id");
      navigate(AuthRoutes.VIEWHANGINGPROTOCOL + "?id=" + id);
    }
    
    const validateTabs = async (currentTabNo, prevTabNo) => {
      setSubmitClicked(false);
      setSelectedTab(currentTabNo);
      if (props.action === ACTIONS.VIEW || currentTabNo === prevTabNo) return;
      console.log(validationSchema);
      validateForm(values).then((errors) => {
        setAllErrors((prevErrors) => ({
          ...prevErrors,
          ...errors,
        }));
        if(prevTabNo === "1") {
          if (errors.name || errors.description) {
            setTabStatusInitialValue(setErrorTabStatus(tabStatusInitialValue, "tab1Status"));
          } else {
            setTabStatusInitialValue(setValidTabStatus(tabStatusInitialValue, "tab1Status"));
          }
        } else if(prevTabNo === "2") {
          if (errors.studyPrerequisiteList) {
            setTabStatusInitialValue(setErrorTabStatus(tabStatusInitialValue, "tab2Status"));
          } else {
            setTabStatusInitialValue(setValidTabStatus(tabStatusInitialValue, "tab2Status"));
          }
        } else if(prevTabNo === "3") {
          if (errors.layout) {
            setTabStatusInitialValue(setErrorTabStatus(tabStatusInitialValue, "tab3Status"));
          } else {
            setTabStatusInitialValue(setValidTabStatus(tabStatusInitialValue, "tab3Status"));
          }
        }

      });
    }

    const tabValidation = (latestVal, previousVal) => {
        validateTabs(latestVal.charAt(latestVal.length - 1), previousVal.charAt(previousVal.length - 1));
        
      };

    const topButtons = [
      {
        key: "1",
        button: (
          <PrimaryButton
            key="1"
            style={{ marginLeft: "10px" }}
            text={t("button.edit")}
            onClick={handleLoadEdit}
          />
        ),
      },
    ];

    const tabDetails = [
        {
            selectedKey: '1',
            tabName: `${t("hanging-protocol.details")}`,
            tabKeyContain: (
                <CustomTab
                    orderNumber="1"
                    tabName={t("hanging-protocol.details")}
                    subtitle={t("sub-title.hanging-protocol.details")}
                    iconName={tabStatusInitialValue.tab1Status.value}
                    iconColor={tabStatusInitialValue.tab1Status.color}
                    action={props.action}
              />
            ),
            tabValueContain: (
                <HangingProtocolDetails
                  key="1"
                  formik = {formik}
                  action={props.action}
                  allErrors={errors}
                />
            ),
        },
        {  
            selectedKey: "2",
            tabName: `${t("hanging-protocol.study-prerequisite")}`,
            tabKeyContain: (
                <CustomTab
                    orderNumber="2"
                    tabName={t("hanging-protocol.study-prerequisite")}
                    subtitle={t("sub-title.hanging-protocol.study-prerequisite")}
                    iconName={tabStatusInitialValue.tab2Status.value}
                    iconColor={tabStatusInitialValue.tab2Status.color}
                    action={props.action}
              />
            ),
            tabValueContain: (
                <HangingProtocolPrerequisite
                  key="2"
                  formik = {formik}
                  studyPrerequisiteOptions = {studyPrerequisiteMetaTags}
                  conditionOperandOptions = {conditionOperands}
                  action={props.action}
                  allErrors={errors}
                />
            ),
        },
        {  
            selectedKey: "3",
            tabName: `${t("hanging-protocol.layout")}`,
            tabKeyContain: (
                <CustomTab
                    orderNumber="3"
                    tabName={t("hanging-protocol.layout")}
                    subtitle={t("sub-title.hanging-protocol.layout")}
                    iconName={tabStatusInitialValue.tab3Status.value}
                    iconColor={tabStatusInitialValue.tab3Status.color}
                    action={props.action}
              />
            ),
            tabValueContain: (
                <HangingProtocolLayout
                  key="3"
                  formik = {formik}
                  seriesConditionMetaTagOptions = {seriesConditionMetaTags}
                  conditionOperandOptions = {conditionOperands}
                  imageConditionMetaTagOptions = {imageConditionMetaTags}
                  viewportFlipOptions = {viewportFlipOptions}
                  viewportAlignmentOptions = {viewportAlignmentOptions}
                  action={props.action}
                  allErrors={errors}
                />
            ),
           
        },        
    ];
    return (
        <>
          {" "}
          {authenticated && (
            <>
                <TabContainer
                uniqueKey={"hangingprotocol_"}
                tabDetails={tabDetails}
                NoOfTabs={tabDetails.length}
                onCancelClick={handleCancel}
                onSubmitClick={handleSave}
                onNextClick={validateTabs}
                previousNextButton={true}
                state={state}
                action={props.action}
                topButtons={ props.action === ACTIONS.VIEW ? topButtons : [] }
                breadCrumbItems={breadCrumbItems}
                tabValidation={tabValidation}
                updateActiveTab={updateActiveTab}
                setUpdateActiveTab={setUpdateActiveTab}
                />
             </>
          )}
        </>
      );

};  

export default HPFormManager
